import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { IComment, ICommentReply, IImage } from '../../services/posts/posts.type';
import { ModalController, IonicModule } from '@ionic/angular';
import { selectCurrentPlatform } from '../../../data/store/selectors/platform.selectors';
import { Store } from '@ngrx/store';
import { NavigationStart, Router } from '@angular/router';
import { filter, first, map, of, Subscription, switchMap, take } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  CurrentPlatform,
  DataType,
  ImagePreviewType,
  ScrollToType,
  TextSizes,
  TextType,
  UserDataImgSize,
  ViewType,
} from '../../enums';
import SwiperCore, { Keyboard as SwiperKeyboard, SwiperOptions } from 'swiper';
import { SwiperComponent, SwiperModule } from 'swiper/angular';
import { UtilsService } from '../../services/utils.service';
import { PhotoReportsService } from '../../services/photo-reports/photo-reports.service';
import {
  selectCommentEditMode,
  selectFavoritesIsLoading,
  selectFavouritesListToBeUpdated,
  selectReplyTo,
} from '../../../data/store/selectors/posts.selectors';
import { ScrollToService } from '../../services/scroll-to/scroll-to.service';
import { selectLikedLIst } from '../../../data/store/selectors/photo-report.selectors';
import deepClone from '../../utils/deepClone';
import { Actions, ofType } from '@ngrx/effects';
import {
  addFileToFavoritesList,
  changeEditCommentMode,
  createReplyToCommentData,
  editComment,
  editCommentSuccess,
  removeFileFromFavoritesList,
} from 'src/app/data/store/actions/posts.actions';
import { addPhotoReportComment } from 'src/app/data/store/actions/photo-report.action';
import { TPushNavigationPayload } from '../../services/push/push.type';
import { IGalleryState } from 'src/app/data/store/models/gallery.model';
import { selectGalleryData } from 'src/app/data/store/selectors/gallery.selectors';
import {
  changeGalleryDataImageIndex,
  resetGalleryData,
  setGalleryImages,
} from 'src/app/data/store/actions/gallery.action';
import { SlidePhotoGalleryFullscreenComponent } from '../slide-photo-gallery-fullscreen/slide-photo-gallery-fullscreen.component';
import { NgClass, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { TextComponent } from '../text/text.component';
import { IconComponent } from '../icon/icon.component';
import { ImageComponent } from '../image/image.component';
import { PostInformationComponent } from '../post-template/post-information/post-information.component';
import { DropdownMenuComponent } from '../post-template/dropdown-menu/dropdown-menu.component';
import { IonScrollBarDirective } from '../../directives/ion-scroll-bar/ion-scroll-bar.directive';
import { PostCommentComponent } from '../post-comment/post-comment.component';
import { AddCommentComponent } from '../add-comment/add-comment.component';
import { UserDataComponent } from '../user-data/user-data.component';
import { SvgComponent } from '../svg/svg.component';
import { GetImagePreviewPipe } from '../../pipe/get-image-preview/get-image-preview.pipe';
import { PhotoViewCounterPipe } from '../../pipe/photo-view-counter/photo-view-counter.pipe';
import { GetImagesLikesCountPipe } from '../../pipe/images-like/get-images-likes-count.pipe';
import { IsImageLikedPipe } from '../../pipe/images-like/is-image-liked.pipe';
import { GetFullNamePipe } from '../../pipe/get-full-name/get-full-name.pipe';

SwiperCore.use([SwiperKeyboard]);

@UntilDestroy()
@Component({
  selector: 'app-slide-photo-gallery-with-comments',
  templateUrl: './slide-photo-gallery-with-comments.component.html',
  styleUrls: ['./slide-photo-gallery-with-comments.component.scss'],
  imports: [
    IonicModule,
    TextComponent,
    IconComponent,
    NgClass,
    NgTemplateOutlet,
    ImageComponent,
    SwiperModule,
    PostInformationComponent,
    DropdownMenuComponent,
    IonScrollBarDirective,
    PostCommentComponent,
    AddCommentComponent,
    UserDataComponent,
    SvgComponent,
    AsyncPipe,
    GetImagePreviewPipe,
    PhotoViewCounterPipe,
    GetImagesLikesCountPipe,
    IsImageLikedPipe,
    GetFullNamePipe,
  ],
})
export class SlidePhotoGalleryWithCommentsComponent implements OnInit, OnDestroy, AfterViewInit {
  private _modalCtrl = inject(ModalController);
  private _store = inject(Store);
  private _cdr = inject(ChangeDetectorRef);
  private _utilsService = inject(UtilsService);
  private _photoReportsService = inject(PhotoReportsService);
  private _scrollToService = inject(ScrollToService);
  private _actions$ = inject(Actions);
  private _router = inject(Router);

  @Input() public commentToScrollData?: TPushNavigationPayload;

  @Input() public slidePhotoGalleryModal?: HTMLIonModalElement;

  @ViewChild('commentsView') webContent?: ElementRef<HTMLDivElement>;

  @ViewChild('columnsView') mobileContent?: ElementRef<HTMLDivElement>;

  @ViewChild('swiper', { static: false }) swiper?: SwiperComponent;

  @ViewChild('commentContainer') commentContainer?: ElementRef<HTMLDivElement>;

  readonly CurrentPlatform = CurrentPlatform;

  readonly TextSizes = TextSizes;

  readonly TextType = TextType;

  readonly UserDataImgSize = UserDataImgSize;

  readonly ViewTypesList = ViewType;

  readonly DataTypesList = DataType;

  readonly ImagePreviewType = ImagePreviewType;

  public galleryData: IGalleryState = { images: [], chosenImgIndex: 0 };

  public favouritesListToBeUpdated$ = this._store.select(selectFavouritesListToBeUpdated);

  public isLoadingFavorite$ = this._store.select(selectFavoritesIsLoading);

  public openImageId?: number;

  public currentPlatform: CurrentPlatform | null = null;

  public currentPlatform$ = this._store.select(selectCurrentPlatform);

  public selectLikedList$ = this._store.select(selectLikedLIst);

  public replyToComment$ = this._store.select(selectReplyTo);

  public imagesList: (IImage & { photoReportId?: number })[] = [];

  public commentToScroll: TPushNavigationPayload | null = null;

  public page = 0;

  public size = 20;

  public isAllCommentsFetched = false;

  public isLoading = true;

  public isFirstLoading = true;

  public comments: IComment[] = [];

  public commentsCount = 0;

  public newComment: IComment | null = null;

  public editCommentId: number | null = null;

  private _totalElements = 0;

  public config?: SwiperOptions;

  private _routerSubscription?: Subscription;

  constructor() {
    this._resetEdit();
    this._resetReply();

    this._routerSubscription = this._router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        if (this.slidePhotoGalleryModal) {
          this.slidePhotoGalleryModal.animated = false;
          this.slidePhotoGalleryModal.dismiss();
        }
      });
  }

  ngOnInit(): void {
    this._store
      .select(selectGalleryData)
      .pipe(untilDestroyed(this))
      .subscribe((galleryData) => {
        if (this.swiper?.swiperRef) {
          this._utilsService.changeSwiperSlide({
            swiper: this.swiper?.swiperRef,
            modalId: 'SlidePhotoGalleryWithCommentsComponent',
            prevIndex: this.galleryData.chosenImgIndex,
            currentIndex: galleryData.chosenImgIndex,
          });
        }

        this.galleryData = galleryData;

        this.config = {
          loop: !this.galleryData.isLoopDisabled && this.galleryData.images.length > 1,
          initialSlide: this.galleryData.chosenImgIndex,
          spaceBetween: 20,
          slidesPerView: 1,
          centeredSlides: true,
        };

        this.imagesList = deepClone(this.galleryData.images);
        this.commentsCount = this.imagesList[this.galleryData.chosenImgIndex]?.commentCount || 0;
        this.commentToScroll = this.commentToScrollData || null;
      });

    this._actions$.pipe(ofType(editCommentSuccess)).subscribe((action) => {
      this._updateMainList(action.commentData, true);
      this._store.dispatch(
        changeEditCommentMode({
          isEditMode: false,
          currentCommentData: {},
        }),
      );
    });

    this.currentPlatform$?.subscribe((platform) => {
      this.currentPlatform = platform;
      this._subscribeToScroll(platform);
    });

    this._store
      .select(selectCommentEditMode)
      .pipe(untilDestroyed(this))
      .subscribe((isEditMode: boolean) => {
        if (!isEditMode) {
          this.editCommentId = null;
        }
      });

    this._modalCtrl
      .getTop()
      .then((modal) => {
        modal
          ?.onWillDismiss()
          .then(() => {
            this._resetEdit();
            this._resetReply();
          })
          .catch(() => {
            // no-op: обработка по умолчанию
          });
      })
      .catch(() => {
        // no-op: обработка по умолчанию
      });
  }

  ngOnDestroy(): void {
    this._routerSubscription?.unsubscribe();
    if (this.commentToScrollData) {
      this._store.dispatch(resetGalleryData());
    }
  }

  ngAfterViewInit(): void {
    this.getComments();
  }

  public getComments(showLastPage?: boolean, slideChanged?: boolean) {
    if (this.galleryData.hideComments || !this.imagesList[this.galleryData.chosenImgIndex]?.id) {
      return;
    }

    this.isLoading = true;
    this._photoReportsService
      .getImagesComments(this.imagesList[this.galleryData.chosenImgIndex].id, {
        page: this.page,
        size: this.size,
        sort: 'id,desc',
      })
      .pipe(first())
      .subscribe((commentsArray) => {
        if (!commentsArray) {
          return;
        }

        this._totalElements = commentsArray.totalElements;

        this.isLoading = false;
        this.isFirstLoading = false;

        if (showLastPage || slideChanged) {
          this.comments = commentsArray.elements;
        } else {
          this.comments = [...this.comments, ...commentsArray.elements];
        }

        this.isAllCommentsFetched = this.comments.length === commentsArray.totalElements;

        this._scrollToComment();
      });
  }

  public createImageData(
    text: string,
    type: string,
    reply: Partial<ICommentReply> | null,
  ): Partial<IComment> {
    if (reply) {
      return { text: text, type: type, replayFor: { type: reply.type, id: reply.id } };
    }
    return { text: text, type: type };
  }

  public createComment(commentData: { text: string; commentId?: number; isEditMode: boolean }) {
    if (!commentData.text) return;

    this.replyToComment$
      .pipe(
        take(1),
        switchMap((reply) => {
          const data = this.createImageData(commentData.text, 'Image', reply);
          if (commentData.isEditMode && commentData.commentId) {
            this._store.dispatch(
              editComment({
                commentId: commentData.commentId,
                commentData: data,
              }),
            );
            return of('');
          } else {
            if (this.imagesList[this.galleryData.chosenImgIndex]?.id) {
              return this._photoReportsService
                .addCommentsToImage(this.imagesList[this.galleryData.chosenImgIndex].id, data)
                .pipe(
                  take(1),
                  map((imagesComment) => {
                    this._updateImageCommentsCount();
                    if (!imagesComment.replayFor) {
                      this._updateMainList(imagesComment);
                    } else {
                      this.newComment = imagesComment;
                    }
                    this._cdr.detectChanges();
                  }),
                );
            }
            return of('');
          }
        }),
      )
      .subscribe();

    this.editCommentId = null;
    this._store.dispatch(
      changeEditCommentMode({
        isEditMode: false,
        currentCommentData: {},
      }),
    );
  }

  public onScroll(event: Event): void {
    const element = event.target as HTMLDivElement;
    if (
      element.scrollHeight - (element.scrollTop + element.clientHeight) <= 100 &&
      !this.isAllCommentsFetched &&
      !this.isLoading
    ) {
      this.page += 1;
      this.getComments();
    }
  }

  public slideChanged() {
    if (
      this.swiper?.swiperRef.realIndex == null ||
      this.swiper?.swiperRef.realIndex === this.galleryData.chosenImgIndex
    ) {
      return;
    }

    this.page = 0;
    this.comments = [];
    this._store.dispatch(changeGalleryDataImageIndex({ index: this.swiper.swiperRef.realIndex }));
    this.openImageId = undefined;
    this.isFirstLoading = true;
    this.getComments(false, true);
    this.commentsCount = this.imagesList[this.galleryData.chosenImgIndex]?.commentCount || 0;
    this._cdr.detectChanges();
  }

  public isFileNotFav(favouritesListToBeUpdated: number[]) {
    const { id, favorite } = this.galleryData.images[this.galleryData.chosenImgIndex || 0] || {};
    const isInFavouritesList = favouritesListToBeUpdated.includes(id);
    return (isInFavouritesList && favorite) || (!isInFavouritesList && !favorite);
  }

  public onFavoriteButtonClick(favouritesListToBeUpdated: number[]) {
    const fileId = this.galleryData.images[this.galleryData.chosenImgIndex || 0].id;
    if (this.isFileNotFav(favouritesListToBeUpdated)) {
      this._store.dispatch(addFileToFavoritesList({ fileId }));
    } else {
      this._store.dispatch(removeFileFromFavoritesList({ fileId }));
    }
  }

  public goBack() {
    this._utilsService.dismissModal();
  }

  public changeCommentEditMode = (comment: IComment, editMode?: boolean) => {
    if (!comment) {
      return;
    }

    if (this.editCommentId === comment.id || editMode === false) {
      this.editCommentId = null;
    } else {
      this.editCommentId = comment.id;
    }

    this._store.dispatch(
      changeEditCommentMode({
        isEditMode: editMode === false ? false : this.editCommentId === comment.id,
        currentCommentData: editMode === false ? {} : comment,
      }),
    );
  };

  public closeModal() {
    this._utilsService.dismissModal();
  }

  public trackByFn(_index: number, comment: IComment) {
    return comment.id;
  }

  public async openFullscreenModal() {
    const modal = await this._modalCtrl.create({
      id: 'SlidePhotoGalleryFullscreenComponent',
      component: SlidePhotoGalleryFullscreenComponent,
      mode: 'ios',
      cssClass: 'fullscreen-gallery-modal',
      animated: false,
      canDismiss: true,
      breakpoints: [0, 1],
    });

    modal.present();
  }

  public async handleBackgroundClick(event: MouseEvent) {
    event.stopPropagation();
  }

  private _scrollToComment() {
    if (!this.comments.length || !this.commentToScroll) {
      return;
    }

    this._utilsService.scrollToComment(this.commentToScroll).then(() => {
      this.commentToScroll = null;
    });
  }

  private _updateImageCommentsCount() {
    if (this.imagesList[this.galleryData.chosenImgIndex]) {
      this._store.dispatch(
        addPhotoReportComment({
          index: this.galleryData.chosenImgIndex,
          category: this.imagesList[this.galleryData.chosenImgIndex].imageCategory,
        }),
      );
      const newImages = [...this.imagesList];
      newImages[this.galleryData.chosenImgIndex].commentCount += 1;
      this.commentsCount = newImages[this.galleryData.chosenImgIndex].commentCount;
      this.imagesList = newImages;
      this._store.dispatch(setGalleryImages({ images: newImages }));
    }
  }

  private _updateMainList(comment: IComment, isEditMode?: boolean) {
    if (isEditMode) {
      const comments = [...this.comments];
      const index = this.comments.findIndex((c) => c.id === comment.id);
      if (index > -1) {
        comments[index] = comment;
        this.comments = comments;
      }
    } else {
      this.comments = [...this.comments, comment];
    }
    this.page = Math.max(0, Math.ceil(this._totalElements / this.size) - 1);
    this.getComments(true);
    this._scrollToBottom();
  }

  private _scrollToBottom() {
    setTimeout(() => {
      this._scrollToService.scrollToEvent({
        scrollTo: ScrollToType.bottom,
        isModalView: true,
      });
    });
  }

  private _subscribeToScroll(platform: CurrentPlatform | null) {
    this._scrollToService
      .scrollToEventListener()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const contentView =
          platform === CurrentPlatform.desktop ? this.webContent : this.mobileContent;

        contentView?.nativeElement.scrollTo({
          top: contentView.nativeElement.scrollHeight,
          behavior: 'smooth',
        });
      });
  }

  private _resetEdit() {
    this._store.dispatch(changeEditCommentMode({ isEditMode: false, currentCommentData: {} }));
  }

  private _resetReply() {
    this._store.dispatch(createReplyToCommentData({ comment: null }));
  }
}
